import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Landmark, LogOut, TrendingUp, Umbrella } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AxiomFlowMark } from './shared/BrandLogo';
import { ContactCard, CONTACT_EMAIL, mailtoHref } from './landing/ContactCard';

/* ------------------------------------------------------------- palette */
const MINT = '#54e6c6';
// Book-a-demo scheduling link (Cal.com)
const CAL_URL = 'https://cal.com/srinivas-chuthari-f3beys/founder-introduction';
// Enable demo invitations at launch with REACT_APP_DEMO_BOOKING_ENABLED=true.
const DEMO_BOOKING_ENABLED = process.env.REACT_APP_DEMO_BOOKING_ENABLED === 'true';
// "Talk to us" opens the enquiry form (ContactCard, posts to /api/public/enquiries); the mailto on the
// same links is the fallback for a reader with scripting off, and the subject becomes the enquiry's topic.
const CONTACT_SUBJECT = 'AxiomFlow: tell me more';
const CONTACT = mailtoHref(CONTACT_SUBJECT);
const T = {
  dark:  { bg: '#08080a', ink: '#f4efe7', sub: 'rgba(244,239,231,0.56)', line: 'rgba(244,239,231,0.13)', card: 'rgba(244,239,231,0.035)' },
  light: { bg: '#efe9dc', ink: '#16150f', sub: 'rgba(22,21,15,0.56)',    line: 'rgba(22,21,15,0.14)',    card: 'rgba(22,21,15,0.04)' }
} as const;
type Mode = keyof typeof T;
const EASE = [0.22, 1, 0.36, 1] as const;
const OUT = [0.16, 1, 0.3, 1] as const;   // strong decel for the mask wipe
// per-beat glow position — each scene gets its own light source
const GLOW_POS: [string, string][] = [
  ['50%', '46%'], ['50%', '38%'], ['64%', '44%'], ['36%', '50%'],
  ['50%', '36%'], ['38%', '46%'], ['62%', '48%'], ['36%', '50%'], ['62%', '48%'], ['50%', '44%']
];

/* ------------------------------------------------------------- cinematic primitives */
const HTag = styled.h1<{ $sm?: boolean }>`
  font-family: 'Fraunces', Georgia, serif; font-weight: 350; letter-spacing: -0.015em; line-height: 1.06;
  font-size: ${p => (p.$sm ? 'clamp(2.2rem, 5.6vw, 4.6rem)' : 'clamp(2.7rem, 7.4vw, 6.4rem)')}; margin: 0;
  em { font-style: italic; font-weight: 400; }
  .mint { color: ${MINT}; }
`;
const Mask = styled.span` display: block; overflow: hidden; padding-bottom: 0.1em; `;
const MInner = styled(motion.span)` display: block; will-change: transform; `;
const Headline: React.FC<{ show: boolean; lines: React.ReactNode[]; small?: boolean }> = ({ show, lines, small }) => (
  <HTag $sm={small}>
    {lines.map((ln, i) => (
      <Mask key={i}>
        <MInner initial={{ y: '120%' }} animate={{ y: show ? '0%' : '120%' }}
          transition={{ duration: 0.95, ease: OUT, delay: show ? 0.04 + i * 0.11 : 0 }}>
          {ln}
        </MInner>
      </Mask>
    ))}
  </HTag>
);

const Fade: React.FC<{ show: boolean; delay?: number; y?: number; children: React.ReactNode }> = ({ show, delay = 0, y = 22, children }) => (
  <motion.div initial={{ opacity: 0, y }} animate={{ opacity: show ? 1 : 0, y: show ? 0 : y }}
    transition={{ duration: 0.7, ease: EASE, delay: show ? delay : 0 }} style={{ pointerEvents: show ? 'auto' : 'none' }}>
    {children}
  </motion.div>
);
const Rise: React.FC<{ show: boolean; delay?: number; x?: number; y?: number; children: React.ReactNode }> = ({ show, delay = 0, x = 0, y = 24, children }) => (
  <motion.div initial={{ opacity: 0, x, y }} animate={{ opacity: show ? 1 : 0, x: show ? 0 : x, y: show ? 0 : y }}
    transition={{ type: 'spring', stiffness: 130, damping: 18, delay: show ? delay : 0 }} style={{ pointerEvents: show ? 'auto' : 'none' }}>
    {children}
  </motion.div>
);
const CountUp: React.FC<{ show: boolean; to: number }> = ({ show, to }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!show) { setV(0); return; }
    let raf = 0; const t0 = performance.now(); const dur = 1000;
    const tick = (t: number) => { const k = Math.min(1, (t - t0) / dur); setV(Math.round(to * (1 - Math.pow(1 - k, 3)))); if (k < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [show, to]);
  return <>{v}</>;
};

/* ------------------------------------------------------------- text bits */
const Eyebrow = styled.div`
  font-family: 'JetBrains Mono', monospace; font-size: 12px; letter-spacing: 0.24em; text-transform: uppercase;
  opacity: 0.62; display: inline-flex; align-items: center; gap: 10px;
  b { width: 6px; height: 6px; border-radius: 50%; background: ${MINT}; box-shadow: 0 0 12px ${MINT}; }
`;
const Sub = styled.p`
  font-family: 'Inter', sans-serif; font-size: clamp(1rem, 1.5vw, 1.2rem); line-height: 1.58; margin: 0;
  max-width: 40rem; color: inherit; opacity: 0.6;
`;
const MonoLabel = styled.div`
  font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; opacity: 0.5;
`;
const CtaRow = styled.div` display: flex; gap: 14px; flex-wrap: wrap; align-items: center; `;
const PrimaryA = styled.a`
  display: inline-flex; align-items: center; gap: 9px; cursor: pointer; border: 0; border-radius: 999px; text-decoration: none;
  background: ${MINT}; color: #04120e; font: 700 15px 'Inter', sans-serif; padding: 15px 26px;
  transition: transform .2s ease, box-shadow .3s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 16px 44px ${MINT}44; }
`;
const Quiet = styled.button`
  display: inline-flex; align-items: center; gap: 7px; cursor: pointer; border-radius: 999px;
  border: 1px solid rgba(244,239,231,0.22); background: transparent; color: inherit; opacity: 0.78;
  font: 600 14px 'Inter', sans-serif; padding: 14px 22px; transition: opacity .2s, border-color .2s;
  &:hover { opacity: 1; border-color: rgba(244,239,231,0.45); }
`;

/* ------------------------------------------------------------- animated data views */
const Blot = styled(motion.div)<{ $m: Mode }>`
  width: min(680px, 92vw); border: 1px solid ${p => T[p.$m].line}; border-radius: 14px; overflow: hidden;
  background: ${p => T[p.$m].card}; backdrop-filter: blur(8px);
  .h { display: flex; align-items: center; justify-content: space-between; padding: 13px 18px; border-bottom: 1px solid ${p => T[p.$m].line};
    font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.6; }
  .h b { display: inline-flex; align-items: center; gap: 7px; color: ${MINT}; opacity: 1; }
  .h b i { width: 6px; height: 6px; border-radius: 50%; background: ${MINT}; box-shadow: 0 0 10px ${MINT}; }
  .cols, .brow { display: grid; grid-template-columns: 1.7fr 1fr 0.9fr 0.7fr 0.9fr; gap: 12px; align-items: center; padding: 11px 18px; }
  .cols { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; opacity: 0.42; }
  .brow { border-top: 1px solid ${p => T[p.$m].line}; font-size: 13px; }
  .brow .cls { opacity: 0.6; }
  .brow .n { font-family: 'JetBrains Mono', monospace; text-align: right; opacity: 0.85; }
  .pill { justify-self: start; font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; padding: 4px 9px; border-radius: 999px; border: 1px solid currentColor; }
`;
const rows: [string, string, string, string, string][] = [
  ['Sponsor Term Loan B', 'Lev Loan', '$12.5M', '99.25', 'Posted'],
  ['Series 2024-1 CLO A', 'CLO Debt', '$8.0M', '100.10', 'Posted'],
  ['CDX.NA.HY.42', 'Index CDS', '$25.0M', '—', 'Executed'],
  ['Unitranche Facility', 'Private Credit', '$15.0M', '98.50', 'Approved'],
  ['Conduit CMBS 2023', 'CMBS', '$6.2M', '94.75', 'Draft']
];
const st = (s: string) => (s === 'Posted' ? MINT : s === 'Executed' ? '#7fb2ff' : s === 'Approved' ? '#e6b980' : 'rgba(244,239,231,0.5)');
const BlotterView: React.FC<{ show: boolean }> = ({ show }) => (
  <Blot $m="dark" initial={{ opacity: 0, y: 16 }} animate={{ opacity: show ? 1 : 0, y: show ? 0 : 16 }} transition={{ duration: 0.5, ease: EASE }}>
    <div className="h"><span>Sample credit book · IBOR</span><b><i />illustration</b></div>
    <div className="cols"><span>Security</span><span>Class</span><span style={{ textAlign: 'right' }}>Notional</span><span style={{ textAlign: 'right' }}>Price</span><span>Status</span></div>
    {rows.map((r, i) => (
      <motion.div className="brow" key={r[0]} initial={{ opacity: 0, y: 14 }} animate={{ opacity: show ? 1 : 0, y: show ? 0 : 14 }}
        transition={{ duration: 0.5, ease: EASE, delay: show ? 0.18 + i * 0.09 : 0 }}>
        <span>{r[0]}</span><span className="cls">{r[1]}</span><span className="n">{r[2]}</span><span className="n">{r[3]}</span>
        <motion.span className="pill" style={{ color: st(r[4]) }} initial={{ scale: 0.8 }} animate={{ scale: show ? 1 : 0.8 }}
          transition={{ type: 'spring', stiffness: 320, damping: 16, delay: show ? 0.34 + i * 0.09 : 0 }}>{r[4]}</motion.span>
      </motion.div>
    ))}
  </Blot>
);

const Cmp = styled(motion.div)<{ $m: Mode }>`
  width: min(680px, 92vw); display: grid; gap: 10px;
  .head { display: grid; grid-template-columns: 1.5fr 1fr 1fr; gap: 12px; padding: 0 18px 2px;
    font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; opacity: 0.42; }
  .row { display: grid; grid-template-columns: 1.5fr 1fr 1fr; gap: 12px; align-items: center; padding: 13px 18px; border-radius: 12px;
    border: 1px solid ${p => T[p.$m].line}; background: ${p => T[p.$m].card}; }
  .row.win { border-color: ${MINT}; background: ${MINT}12; }
  .row strong { font: 600 15px 'Inter', sans-serif; }
  .row span { font-family: 'JetBrains Mono', monospace; font-size: 12.5px; opacity: 0.75; }
  .row.win span { color: ${MINT}; opacity: 1; }
  .row.bad span { color: #c0603f; opacity: 0.95; }
`;
const cmp: [string, string, string, string][] = [
  ['Spreadsheets & shared drives', 'Manual handoffs', 'Fragmented view', ''],
  ['Separate systems', 'Separate workflows', 'More reconciliation', 'bad'],
  ['AxiomFlow', 'One platform', 'One credit book', 'win']
];
const CmpView: React.FC<{ show: boolean }> = ({ show }) => (
  <Cmp $m="light" initial={{ opacity: 0 }} animate={{ opacity: show ? 1 : 0 }} transition={{ duration: 0.4 }}>
    <div className="head"><span>Approach</span><span>Workflow</span><span>View</span></div>
    {cmp.map((r, i) => (
      <motion.div className={`row ${r[3]}`} key={r[0]} initial={{ opacity: 0, y: 16 }} animate={{ opacity: show ? 1 : 0, y: show ? 0 : 16 }}
        transition={{ duration: 0.55, ease: EASE, delay: show ? 0.15 + i * 0.12 : 0 }}>
        <strong>{r[0]}</strong><span>{r[1]}</span><span>{r[2]}</span>
      </motion.div>
    ))}
  </Cmp>
);

const CovGrid = styled(motion.div)<{ $m: Mode }>`
  width: min(760px, 94vw); display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px;
  @media (max-width: 760px){ grid-template-columns: repeat(2, 1fr); }
  .c { border: 1px solid ${p => T[p.$m].line}; border-radius: 12px; padding: 18px 14px; text-align: center; background: ${p => T[p.$m].card}; }
  .c b { display: block; font-family: 'Fraunces', serif; font-weight: 400; font-size: 2.8rem; line-height: 1; color: ${MINT}; }
  .c span { display: block; margin-top: 8px; font: 500 12px 'Inter', sans-serif; opacity: 0.7; line-height: 1.3; }
`;
const cov: [number, string][] = [[6, 'Public credit'], [7, 'Loans & private'], [6, 'Structured'], [6, 'Derivatives'], [4, 'Distressed & RE']];
const CovView: React.FC<{ show: boolean }> = ({ show }) => (
  <>
    <CovGrid $m="dark" initial={{ opacity: 0 }} animate={{ opacity: show ? 1 : 0 }} transition={{ duration: 0.4 }}>
      {cov.map((c, i) => (
        <motion.div className="c" key={c[1]} initial={{ opacity: 0, y: 18 }} animate={{ opacity: show ? 1 : 0, y: show ? 0 : 18 }}
          transition={{ duration: 0.5, ease: EASE, delay: show ? 0.08 + i * 0.09 : 0 }}>
          <b><CountUp show={show} to={c[0]} /></b><span>{c[1]}</span>
        </motion.div>
      ))}
    </CovGrid>
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: show ? 1 : 0, y: show ? 0 : 14 }}
      transition={{ duration: 0.5, ease: EASE, delay: show ? 0.55 : 0 }} style={{ width: 'min(760px, 94vw)', marginTop: 14 }}>
      <div style={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', border: '1px solid rgba(244,239,231,0.14)' }}>
        {[24, 28, 20, 16, 12].map((w, i) => (
          <motion.div key={i} initial={{ scaleX: 0 }} animate={{ scaleX: show ? 1 : 0 }} transition={{ duration: 0.7, ease: EASE, delay: show ? 0.65 + i * 0.08 : 0 }}
            style={{ width: `${w}%`, background: MINT, opacity: 0.88 - i * 0.16, transformOrigin: 'left' }} />
        ))}
      </div>
      <div style={{ marginTop: 9, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.5, textAlign: 'center' }}>29 instrument types · one posting engine</div>
    </motion.div>
  </>
);

const FeatRow = styled.div<{ $m: Mode }>`
  width: min(660px, 92vw); display: grid; grid-template-columns: 76px 1fr; gap: 16px; align-items: baseline;
  padding: 13px 4px; border-top: 1px solid ${p => T[p.$m].line};
  .k { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.08em; color: ${MINT}; white-space: nowrap; }
  h3 { font: 400 clamp(1.1rem,2.2vw,1.45rem) 'Fraunces', serif; margin: 0 0 4px; letter-spacing: -0.01em; }
  p { margin: 0; font: 400 0.88rem 'Inter', sans-serif; opacity: 0.55; line-height: 1.45; }
`;
const Segs = styled(motion.div)<{ $m: Mode }>`
  width: min(720px, 94vw); display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  @media (max-width: 720px){ grid-template-columns: 1fr; }
  .s { border: 1px solid ${p => T[p.$m].line}; border-radius: 14px; padding: 22px; background: ${p => T[p.$m].card}; height: 100%; }
  .s h3 { font: 400 1.35rem 'Fraunces', serif; margin: 0 0 8px; }
  .s p { margin: 0; font: 400 0.92rem 'Inter', sans-serif; opacity: 0.55; line-height: 1.5; }
`;
const segs: { icon: React.FC<any>; t: string; d: string }[] = [
  { icon: TrendingUp, t: 'Credit Hedge Funds', d: 'Bonds, ABF, Loans, CLO, CDS, distressed, private credit — one book across every desk.' },
  { icon: Landmark, t: 'Asset Managers', d: 'Public & private mandates with what-if compliance and reporting.' },
  { icon: Umbrella, t: 'Insurance & Re', d: 'Economic ALM, book yield, and capital across the general account.' }
];
const SegsView: React.FC<{ show: boolean }> = ({ show }) => (
  <Segs $m="dark" initial={{ opacity: 0 }} animate={{ opacity: show ? 1 : 0 }} transition={{ duration: 0.4 }}>
    {segs.map((sg, i) => { const Icon = sg.icon; return (
      <motion.div className="s" key={sg.t} initial={{ opacity: 0, y: 22 }} animate={{ opacity: show ? 1 : 0, y: show ? 0 : 22 }}
        transition={{ type: 'spring', stiffness: 120, damping: 18, delay: show ? 0.1 + i * 0.11 : 0 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, display: 'grid', placeItems: 'center', border: `1px solid ${MINT}44`, background: `${MINT}12`, color: MINT, marginBottom: 12 }}><Icon size={18} /></div>
        <h3>{sg.t}</h3><p>{sg.d}</p>
      </motion.div>
    ); })}
  </Segs>
);

/* ------------------------------------------------------------- hero product mock */
const Dash = styled(motion.div)`
  width: min(480px, 100%); border: 1px solid rgba(244,239,231,0.14); border-radius: 16px; overflow: hidden;
  background: linear-gradient(180deg, rgba(244,239,231,0.05), rgba(244,239,231,0.02));
  backdrop-filter: blur(10px); box-shadow: 0 44px 120px rgba(0,0,0,0.55);
  .hd { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid rgba(244,239,231,0.12);
    font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.65; }
  .hd b { color: ${MINT}; display: inline-flex; gap: 6px; align-items: center; opacity: 1; }
  .hd b i { width: 6px; height: 6px; border-radius: 50%; background: ${MINT}; box-shadow: 0 0 10px ${MINT}; }
  .kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: rgba(244,239,231,0.09); border-bottom: 1px solid rgba(244,239,231,0.09); }
  .kpi { background: #0b0c0e; padding: 12px 14px; }
  .kpi small { display: block; font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; opacity: 0.45; margin-bottom: 4px; }
  .kpi b { font-family: 'Fraunces', serif; font-weight: 400; font-size: 1.22rem; }
  .kpi b.mint { color: ${MINT}; }
  .chart { padding: 10px 14px 2px; }
  .r { display: grid; grid-template-columns: 1.6fr 0.9fr 0.8fr; gap: 10px; padding: 9px 16px; font-size: 11.5px; border-top: 1px solid rgba(244,239,231,0.08); align-items: center; }
  .r .n { font-family: 'JetBrains Mono', monospace; text-align: right; opacity: 0.8; }
  .r .pill { justify-self: end; font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase; padding: 3px 8px; border-radius: 999px; border: 1px solid currentColor; }
`;
const DashWrap = styled(motion.div)`
  display: grid; justify-items: center;
  @media (max-width: 1020px){ display: none; }
`;
const DashMock: React.FC<{ show: boolean }> = ({ show }) => (
  <DashWrap initial={{ opacity: 0, y: 26, scale: 0.97 }} animate={{ opacity: show ? 1 : 0, y: show ? 0 : 26, scale: show ? 1 : 0.97 }} transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}>
    <Dash animate={{ y: [0, -9, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
      <div className="hd"><span>AxiomFlow · sample credit book</span><b><i />illustration</b></div>
      <div className="kpis">
        <div className="kpi"><small>NAV</small><b>$2.14B</b></div>
        <div className="kpi"><small>MTD P&amp;L</small><b className="mint">+$3.2M</b></div>
        <div className="kpi"><small>Positions</small><b>1,284</b></div>
      </div>
      <div className="chart">
        <svg viewBox="0 0 420 104" width="100%" height="104">
          <defs>
            <linearGradient id="mgrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={MINT} stopOpacity="0.32" />
              <stop offset="1" stopColor={MINT} stopOpacity="0" />
            </linearGradient>
          </defs>
          {[26, 52, 78].map(y => <line key={y} x1="0" y1={y} x2="420" y2={y} stroke="rgba(244,239,231,0.08)" strokeWidth="1" />)}
          <path d="M0,84 C48,76 84,82 128,64 C168,48 202,58 244,44 C286,30 324,38 366,24 L420,18 L420,104 L0,104 Z" fill="url(#mgrad)" />
          <motion.path d="M0,84 C48,76 84,82 128,64 C168,48 202,58 244,44 C286,30 324,38 366,24 L420,18"
            stroke={MINT} strokeWidth="2" fill="none"
            initial={{ pathLength: 0 }} animate={{ pathLength: show ? 1 : 0 }} transition={{ duration: 1.4, ease: EASE, delay: 0.5 }} />
        </svg>
      </div>
      <div>
        <div className="r"><span>Sponsor Term Loan B</span><span className="n">$12.5M</span><span className="pill" style={{ color: MINT }}>Posted</span></div>
        <div className="r"><span>Series 2024-1 CLO A</span><span className="n">$8.0M</span><span className="pill" style={{ color: MINT }}>Posted</span></div>
        <div className="r"><span>CDX.NA.HY.42</span><span className="n">$25.0M</span><span className="pill" style={{ color: '#7fb2ff' }}>Executed</span></div>
      </div>
    </Dash>
  </DashWrap>
);

const HeroGrid = styled.div`
  width: 100%; display: grid; grid-template-columns: 1.04fr 0.96fr; gap: clamp(28px, 4vw, 60px); align-items: center;
  @media (max-width: 1020px){ grid-template-columns: 1fr; }
`;
const HeroScene: React.FC<{ p: number; nav: () => void; authed: boolean }> = ({ p, nav, authed }) => (
  <HeroGrid>
    <div style={{ display: 'grid', gap: 'clamp(18px, 3vh, 28px)', justifyItems: 'start', textAlign: 'left' }}>
      <Fade show={p >= 0}><Eyebrow><b />AI-native credit platform</Eyebrow></Fade>
      <Headline small show={p >= 0} lines={['One book for', <>all of <span className="mint">credit.</span></>]} />
      <Fade show={p >= 1}><Sub>One bi-temporal IBOR for public, private, structured, and derivative credit — every acquisition a governed trade, AI-native from the first ticket.</Sub></Fade>
      <Fade show={p >= 1}><CtaRow>{DEMO_BOOKING_ENABLED && <PrimaryA href={CAL_URL} target="_blank" rel="noopener noreferrer">Book a demo <ArrowRight size={17} /></PrimaryA>}<Quiet onClick={nav}>{authed ? 'Enter platform' : 'Sign in'}</Quiet></CtaRow></Fade>
    </div>
    <DashMock show={p >= 0} />
  </HeroGrid>
);

/* ------------------------------------------------------------- dim-stack (teleprompter focus) */
const SLine = styled(motion.div)`
  font-family: 'Fraunces', Georgia, serif; font-weight: 350; letter-spacing: -0.012em; line-height: 1.14;
  font-size: clamp(1.5rem, 3.1vw, 2.7rem); text-align: left; will-change: transform, opacity;
`;
/* the disconnected-systems pile — one clean panel column (hero-style), a new window lands per line */
const PileWrap = styled.div`
  position: relative; display: grid; place-items: center; height: clamp(300px, 44vh, 430px);
  & > div { grid-area: 1 / 1; }
  @media (max-width: 1020px){ display: none; }
`;
const Pane = styled(motion.div)`
  width: min(400px, 100%); border-radius: 12px; overflow: hidden; pointer-events: none;
  border: 1px solid rgba(244,239,231,0.16); background: rgba(12,13,15,0.94); backdrop-filter: blur(6px);
  box-shadow: 0 34px 90px rgba(0,0,0,0.6); font-family: 'JetBrains Mono', monospace; color: rgba(244,239,231,0.8);
  .tb { display: flex; gap: 7px; align-items: center; padding: 10px 13px; border-bottom: 1px solid rgba(244,239,231,0.12); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; }
  .tb i { width: 8px; height: 8px; border-radius: 2px; border: 1px solid rgba(244,239,231,0.4); flex: none; }
  .bd { padding: 13px 15px; display: grid; gap: 8px; }
  .ln { height: 8px; border-radius: 2px; background: rgba(244,239,231,0.14); }
`;
const artifacts: { dx: number; dy: number; rot: number; el: React.ReactNode }[] = [
  { dx: -20, dy: -18, rot: -2.5, el: (<>
    <div className="tb"><i />TRM 4.2 — BOND BLOTTER</div>
    <div className="bd" style={{ fontSize: 10.5, gap: 7 }}>
      <div>912828XG&nbsp;&nbsp;&nbsp;5,000&nbsp;&nbsp;&nbsp;99-16+</div>
      <div>037833DT&nbsp;&nbsp;&nbsp;2,250&nbsp;&nbsp;&nbsp;101.42</div>
      <div>64966QJP&nbsp;&nbsp;&nbsp;1,000&nbsp;&nbsp;&nbsp;98.875</div>
      <div>17275RBJ&nbsp;&nbsp;&nbsp;7,500&nbsp;&nbsp;&nbsp;96.250&nbsp;<span style={{ color: MINT }}>▌</span></div>
    </div>
  </>) },
  { dx: 22, dy: -16, rot: 2.5, el: (<>
    <div className="tb"><i />abf_borrowing_base_FINAL_v7.xlsx</div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'rgba(244,239,231,0.12)', padding: 1 }}>
      {['Pool', 'Tape', 'Loaded', 'REVIEW', 'Base', 'BBC', '#REF!', 'CHECK', 'Bank', 'Cash', 'Pending', 'MATCH', 'Terms', 'Amend.', 'Version?', 'VERIFY'].map((c, i) => (
        <div key={i} style={{ background: '#0c0d0f', padding: '5px 8px', fontSize: 9.5, color: c === '#REF!' ? '#e8836f' : 'rgba(244,239,231,0.55)' }}>{c}</div>
      ))}
    </div>
  </>) },
  { dx: 18, dy: -8, rot: 2, el: (<>
    <div className="tb"><i />LOANSERV v9 — FACILITIES</div>
    <div className="bd">
      <div style={{ fontSize: 10, opacity: 0.6 }}>TERM LOAN B · REVOLVER · DDTL</div>
      <div className="ln" style={{ width: '88%' }} /><div className="ln" style={{ width: '64%' }} /><div className="ln" style={{ width: '76%' }} /><div className="ln" style={{ width: '52%' }} />
    </div>
  </>) },
  { dx: -14, dy: 10, rot: -1.5, el: (<>
    <div className="tb" style={{ background: '#e9e2d3', color: '#22201a', borderBottom: '1px solid rgba(22,21,15,0.15)' }}><i style={{ borderColor: 'rgba(22,21,15,0.4)' }} />Monthly Trustee Report — May</div>
    <div className="bd" style={{ background: '#e9e2d3' }}>
      <div className="ln" style={{ background: 'rgba(22,21,15,0.18)', width: '92%' }} /><div className="ln" style={{ background: 'rgba(22,21,15,0.18)', width: '78%' }} /><div className="ln" style={{ background: 'rgba(22,21,15,0.18)', width: '84%' }} /><div className="ln" style={{ background: 'rgba(22,21,15,0.18)', width: '58%' }} />
      <div style={{ fontSize: 9, color: 'rgba(22,21,15,0.55)', marginTop: 2 }}>PAGE 1 OF 142</div>
    </div>
  </>) },
  { dx: 0, dy: 6, rot: -1, el: (<>
    <div className="tb"><i />INBOX (247)</div>
    <div className="bd" style={{ gap: 9 }}>
      <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: MINT, flex: 'none' }} /><span style={{ fontSize: 10 }}>RE: RE: FW: Q3 marks FINAL_v7</span></div>
      <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(244,239,231,0.35)', flex: 'none' }} /><span style={{ fontSize: 10, opacity: 0.65 }}>Covenant waiver — signed copy?</span></div>
      <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(244,239,231,0.35)', flex: 'none' }} /><span style={{ fontSize: 10, opacity: 0.65 }}>Wire details for Tuesday close</span></div>
    </div>
  </>) }
];
const stackLines = ['Bonds in a legacy OMS.', 'ABF in a spreadsheet.', 'Loans in a second system.', 'CLOs in trustee reports.', 'Private credit in email.'];
const NL = stackLines.length;
// phases 0..NL-1: lines land one per step, previous lines dim · NL: lines clear, the verdict wipes in · NL+1: the punch
const ProblemStack: React.FC<{ p: number }> = ({ p }) => {
  const cleared = p >= NL;
  return (
    <div style={{ position: 'relative', width: '100%', minHeight: '54vh', display: 'grid', alignContent: 'center' }}>
      <HeroGrid>
        <motion.div animate={{ opacity: cleared ? 0 : 1, y: cleared ? -16 : 0 }} transition={{ duration: 0.55, ease: EASE }}
          style={{ display: 'grid', gap: 'clamp(12px, 2.2vh, 22px)', justifyItems: 'start', textAlign: 'left' }}>
          <MonoLabel>The problem</MonoLabel>
          {stackLines.map((t, i) => (
            <SLine key={t} initial={{ opacity: 0, y: 26, filter: 'blur(8px)' }}
              animate={p >= i
                ? { opacity: p === i ? 1 : 0.26, y: 0, filter: 'blur(0px)' }
                : { opacity: 0, y: 26, filter: 'blur(8px)' }}
              transition={{ duration: 0.6, ease: EASE }}>
              {t}
            </SLine>
          ))}
        </motion.div>
        <PileWrap>
          {artifacts.map((a, i) => (
            <motion.div key={i} style={{ zIndex: i + 1 }}
              initial={{ opacity: 0, x: a.dx, y: a.dy + 46, rotate: a.rot, scale: 0.94 }}
              animate={cleared
                ? { opacity: 0.12, x: a.dx, y: a.dy, rotate: a.rot, scale: 0.96, filter: 'blur(2px)' }
                : p >= i
                  ? { opacity: p === i ? 1 : 0.45, x: a.dx, y: a.dy, rotate: a.rot, scale: p === i ? 1 : 0.975, filter: p === i ? 'blur(0px)' : 'blur(1px)' }
                  : { opacity: 0, x: a.dx, y: a.dy + 46, rotate: a.rot, scale: 0.94 }}
              transition={{ duration: 0.6, ease: EASE }}>
              <Pane>{a.el}</Pane>
            </motion.div>
          ))}
        </PileWrap>
      </HeroGrid>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', zIndex: 30 }}>
        <div style={{ display: 'grid', gap: 20, justifyItems: 'center', textAlign: 'center' }}>
          <Headline show={p >= NL} lines={['A dozen disconnected', 'businesses.']} />
          <Headline show={p >= NL + 1} lines={[<>None of it <span className="mint">reconciles.</span></>]} />
        </div>
      </div>
    </div>
  );
};
const KickLine = styled.div`
  font-family: 'Fraunces', Georgia, serif; font-weight: 350; font-size: clamp(1.25rem, 2.4vw, 1.8rem);
  text-align: center; opacity: 0.85; margin-top: 4px;
  em { font-style: italic; }
`;

/* ------------------------------------------------------------- middle-office scene (feature panel swaps per click) */
const featPanels: React.ReactNode[] = [
  (<>
    <div className="tb"><i />RFQ — SPONSOR TLB · $5.0M</div>
    <div className="bd" style={{ gap: 8, fontSize: 10.5 }}>
      {[['Venue A', '99.20', false], ['Venue B', '99.25', true], ['Venue C', '99.18', false]].map(([v, px, best]) => (
        <div key={v as string} style={{ display: 'flex', justifyContent: 'space-between', color: best ? MINT : undefined }}>
          <span>{v}</span><span>{px}{best ? '  ·  BEST' : ''}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(244,239,231,0.1)', paddingTop: 8, fontSize: 10 }}>
        <span style={{ opacity: 0.55 }}>STATUS</span><span style={{ color: MINT }}>FILLED @ 99.25</span>
      </div>
    </div>
  </>),
  (<>
    <div className="tb"><i />THREE-WAY RECON — T+1</div>
    <div className="bd" style={{ gap: 7, fontSize: 10.5 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr', opacity: 0.5, fontSize: 9.5 }}><span>SOURCE</span><span>POSITIONS</span><span>CASH</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr' }}><span>IBOR</span><span>1,284</span><span>$42.1M</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr' }}><span>Custodian</span><span>1,284</span><span>$42.1M</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr' }}><span>Fund admin</span><span>1,284</span><span style={{ color: '#e8836f' }}>$41.9M</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(244,239,231,0.1)', paddingTop: 8, fontSize: 10 }}>
        <span style={{ opacity: 0.55 }}>1 BREAK SURFACED</span><span style={{ color: MINT }}>→ RESOLVED</span>
      </div>
    </div>
  </>),
  (<>
    <div className="tb"><i />CREDIT_AGREEMENT_2026.PDF</div>
    <div className="bd" style={{ gap: 7, fontSize: 10.5 }}>
      <div style={{ opacity: 0.5, fontSize: 9.5 }}>EXTRACTED → TERMS FOR REVIEW</div>
      {['Facility · $250M Term Loan B', 'Margin · SOFR + 425', 'Maturity · Jun 2031', 'Covenant · Net leverage ≤ 4.5×'].map(f => (
        <div key={f} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}><span>{f}</span><span style={{ color: MINT }}>✓</span></div>
      ))}
    </div>
  </>)
];
const MuscleScene: React.FC<{ p: number }> = ({ p }) => {
  const active = Math.max(0, Math.min(2, p - 1));
  return (
    <HeroGrid>
      <div style={{ display: 'grid', gap: 'clamp(10px, 1.8vh, 18px)', justifyItems: 'start', textAlign: 'left' }}>
        <Fade show={p >= 0}><MonoLabel>The middle office, built in</MonoLabel></Fade>
        <Headline small show={p >= 0} lines={['Not just a book.', 'The muscle', 'around it.']} />
        <div style={{ width: '100%' }}>
          <Rise show={p >= 1} x={-34}><FeatRow $m="dark" style={{ width: '100%' }}><span className="k">FIX</span><div><h3>FIX connectivity</h3><p>Route orders to electronic execution venues over FIX — RFQ workflow, competing quotes, and TCA built in.</p></div></FeatRow></Rise>
          <Rise show={p >= 2} x={-34}><FeatRow $m="dark" style={{ width: '100%' }}><span className="k">RECON</span><div><h3>Three-way reconciliation</h3><p>Your IBOR against custodians, agent banks, and fund admins. Breaks surfaced and resolved, not buried.</p></div></FeatRow></Rise>
          <Rise show={p >= 3} x={-34}><FeatRow $m="dark" style={{ width: '100%' }}><span className="k">INGEST</span><div><h3>Document-to-IBOR</h3><p>Review terms, covenants and cash-flow information from the agreement, with source evidence and approval before booking.</p></div></FeatRow></Rise>
        </div>
      </div>
      <PileWrap>
        {featPanels.map((el, i) => (
          <motion.div key={i} style={{ zIndex: i === active ? 2 : 1 }}
            initial={{ opacity: 0, y: 18 }}
            animate={p >= 1
              ? (i === active ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: -12, scale: 0.97 })
              : (i === 0 ? { opacity: 0.22, y: 0, scale: 0.98 } : { opacity: 0, y: 18 })}
            transition={{ duration: 0.5, ease: EASE }}>
            <Pane>{el}</Pane>
          </motion.div>
        ))}
      </PileWrap>
    </HeroGrid>
  );
};

/* ------------------------------------------------------------- two ABF chapters */
const ABFLayout = styled.div`
  width: 100%; display: grid; grid-template-columns: 1.1fr .9fr; gap: clamp(28px, 4vw, 60px); align-items: center;
  .copy { display: grid; gap: 22px; text-align: left; }
  .feature { min-height: 108px; }
  .feature h2 { font: 400 clamp(1.35rem, 2.4vw, 1.9rem)/1.2 'Fraunces', Georgia, serif; margin: 0 0 12px; }
  .feature p { font: 400 .95rem/1.6 'Inter', sans-serif; margin: 0; opacity: .65; }
  @media(max-width: 800px) {
    grid-template-columns: 1fr; gap: 20px;
    .copy { gap: 14px; }
    h1 { font-size: clamp(1.9rem, 6.4vw, 3rem); }
    .feature { min-height: 86px; }
    .feature h2 { font-size: 1.15rem; margin-bottom: 8px; }
    .feature p { font-size: .8rem; }
  }
`;
const ABFSteps = styled.nav<{ $m: Mode }>`
  display: flex; flex-wrap: wrap; gap: 9px; align-items: center;
  button { border: 1px solid ${p => T[p.$m].line}; background: transparent; color: inherit;
    cursor: pointer; border-radius: 999px; padding: 7px 10px; font: 500 10px 'Inter', sans-serif; opacity: .5; }
  button[aria-current='step'] { opacity: 1; border-color: ${p => p.$m === 'light' ? '#18664f' : MINT}; color: ${p => p.$m === 'light' ? '#18664f' : MINT}; }
  button:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
  .more { display: inline-flex; align-items: center; gap: 6px; margin-left: 6px; color: ${p => p.$m === 'light' ? '#18664f' : MINT}; font: 600 12px 'Inter', sans-serif; text-decoration: none; opacity: .9; }
  .more:hover { opacity: 1; text-decoration: underline; }
`;
const ABFPanel = styled.div<{ $m: Mode }>`
  --abf-accent: ${p => p.$m === 'light' ? '#18664f' : MINT};
  border: 1px solid ${p => T[p.$m].line}; background: ${p => T[p.$m].card}; border-radius: 16px; padding: 22px; width: 100%; position: relative; overflow: hidden;
  .head { font: 500 10px 'JetBrains Mono', monospace; letter-spacing: .1em; text-transform: uppercase; opacity: .55; }
  h3 { font: 400 clamp(1.2rem, 2.1vw, 1.6rem)/1.25 'Fraunces', Georgia, serif; margin: 4px 0 14px; }
  .row { display: flex; align-items: center; justify-content: space-between; gap: 18px; border-top: 1px solid ${p => T[p.$m].line}; padding: 10px 0; font-size: 11px; }
  .row b { font: 400 1.5rem 'Fraunces', Georgia, serif; white-space: nowrap; }
  .accent { color: var(--abf-accent); }
  .flow { display: grid; gap: 8px; }
  .flow div { display: flex; align-items: center; gap: 12px; font-size: 12px; }
  .flow b { font: 500 9px 'JetBrains Mono', monospace; color: var(--abf-accent); }
  .note { font: 400 10px/1.5 'Inter', sans-serif; margin: 16px 0 0; opacity: .55; }
  .visual { width: 100%; height: 224px; display: block; color: var(--abf-accent); overflow: visible; }
  .visual text { fill: currentColor; stroke: none; font: 10px 'JetBrains Mono', monospace; }
  .scan { position: absolute; inset: 0; pointer-events: none; background: linear-gradient(100deg, transparent 30%, ${p => p.$m === 'dark' ? '#54e6c610' : '#18664f0c'} 50%, transparent 70%); }
  @media(max-width: 800px) {
    padding: 16px; .head { font-size: 9px; } h3 { font-size: 1.15rem; margin-bottom: 12px; }
    .visual { height: 150px; } .row { padding: 7px 0; font-size: 10px; } .row b { font-size: 1.3rem; }
    .flow { gap: 6px; } .flow div { font-size: 11px; }
    .note { font-size: 9px; margin-top: 12px; }
  }
`;
const ABFArt: React.FC<{ page: number; active: number; reduced: boolean }> = ({ page, active, reduced }) => {
  const enter = reduced ? false : { opacity: 0, scale: .72, rotate: -14, filter: 'blur(8px)' };
  const leave = reduced ? { opacity: 0 } : { opacity: 0, scale: 1.12, rotate: 9, filter: 'blur(6px)' };
  const document = (x: number, y: number, label: string) => <g><rect x={x} y={y} width="72" height="92" rx="6" fill="currentColor" fillOpacity=".04" /><path d={`M${x+13},${y+20}h44 M${x+13},${y+34}h35 M${x+13},${y+48}h44 M${x+13},${y+62}h27`} opacity=".5" /><text x={x+36} y={y+110} textAnchor="middle">{label}</text></g>;
  const node = (x: number, y: number, label: string) => <g><rect x={x-40} y={y-17} width="80" height="34" rx="6" fill="currentColor" fillOpacity=".04" /><text x={x} y={y+4} textAnchor="middle">{label}</text></g>;
  const chapterOne = [
    <g key="terms">{document(85, 37, 'AGREEMENT')}<motion.path d="M170,70h55 M170,98h38 M170,126h55" strokeWidth="3" initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, delay: .4 }} /><rect x="167" y="151" width="70" height="20" rx="4" fill="currentColor" fillOpacity=".15" /><text x="202" y="165" textAnchor="middle">REVIEW</text></g>,
    <g key="pool">{Array.from({length: 20}, (_, i) => <motion.rect key={i} x={87+(i%5)*31} y={50+Math.floor(i/5)*30} width="23" height="21" rx="3" fill="currentColor" fillOpacity={i===8||i===16 ? .03 : .18} strokeDasharray={i===8||i===16 ? '3 3' : undefined} initial={reduced ? false : { opacity: 0, scale: .3 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: .5, delay: i*.035 }} />)}<text x="160" y="191" textAnchor="middle">LOAN-LEVEL REVIEW</text></g>,
    <g key="base"><text x="76" y="59">CERTIFICATE</text><text x="76" y="118">RECALCULATED</text><motion.rect x="76" y="69" width="175" height="22" rx="3" fill="currentColor" fillOpacity=".2" initial={reduced ? false : { scaleX: 0 }} animate={{ scaleX: 1 }} style={{ transformOrigin: '76px 80px' }} transition={{ duration: 1.1 }} /><motion.rect x="76" y="128" width={175*50.7/51.7} height="22" rx="3" fill="currentColor" fillOpacity=".7" initial={reduced ? false : { scaleX: 0 }} animate={{ scaleX: 1 }} style={{ transformOrigin: '76px 140px' }} transition={{ duration: 1.1, delay: .25 }} /><path d="M253,91v60h-7" strokeDasharray="3 3" /><text x="160" y="185" textAnchor="middle">COMPARE THE EVIDENCE</text></g>,
    <g key="funding">{node(91,65,'REQUEST')}{node(231,155,'FUNDING')}<motion.path d="M131,65h29v90h31" initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.3 }} /><path d="M160,80l22,10v21c0,15-22,27-22,27s-22-12-22-27V90z" fill="currentColor" fillOpacity=".08" /><text x="160" y="112" textAnchor="middle">?</text><text x="160" y="195" textAnchor="middle">CONTROLS BEFORE RELEASE</text></g>,
    <g key="offer">{document(70,47,'OFFER')}{document(180,47,'PURCHASE')}<motion.path d="M150,90h22m-7-6l7,6-7,6" strokeWidth="2" initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1, delay: .5 }} /><text x="160" y="195" textAnchor="middle">REVIEW · ASSIGN · PAY</text></g>
  ];
  const chapterTwo = [
    <g key="cash">{node(90,52,'SERVICER')}{node(90,110,'REMITTANCE')}{node(90,168,'BANK')}<motion.path d="M130,52h40v58h30 M130,110h70 M130,168h40v-58" initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.4 }} /><circle cx="223" cy="110" r="22" fill="currentColor" fillOpacity=".12" /><text x="223" y="114" textAnchor="middle">≈</text></g>,
    <g key="waterfall">{[0,1,2].map(i => <motion.g key={i} initial={reduced ? false : { opacity: 0, scaleX: .3 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ duration: .7, delay: i*.22 }}><rect x={82+i*25} y={50+i*45} width={157-i*25} height="29" rx="4" fill="currentColor" fillOpacity={.1+i*.13} /><text x={96+i*25} y={69+i*45}>{['PRIORITY','DISTRIBUTION','PERIOD CLOSE'][i]}</text></motion.g>)}<motion.path d="M91,80v11h25v30h25v15" initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.4, delay: .4 }} /></g>,
    <g key="change">{document(68,42,'PRIOR')}{document(181,42,'CHANGED')}<motion.path d="M148,82h24m-7-6l7,6-7,6" strokeWidth="2" initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.1 }} /><path d="M60,185h205 M68,181v8 M252,181v8" opacity=".4" /><text x="160" y="208" textAnchor="middle">HISTORY RETAINED</text></g>,
    <g key="report">{document(77,44,'REPORT')}<motion.path d="M161,90h34m-7-6l7,6-7,6" initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2 }} /><rect x="204" y="67" width="46" height="48" rx="6" fill="currentColor" fillOpacity=".08" /><path d="M211,78l16,12,16-12" /><text x="227" y="157" textAnchor="middle">HAND-OFF</text></g>,
    <g key="oversight">{[[94,57],[231,57],[94,166],[231,166]].map(([x,y],i)=><motion.g key={i} initial={reduced ? false : { opacity: 0, scale: .4 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: .6, delay: i*.14 }}><path d={`M160,110L${x},${y}`} opacity=".3" /><rect x={x-29} y={y-17} width="58" height="34" rx="6" fill="currentColor" fillOpacity=".07" /><path d={`M${x-16},${y-5}h32 M${x-16},${y+5}h21`} opacity=".5" /></motion.g>)}<circle cx="160" cy="110" r="22" fill="currentColor" fillOpacity=".2" /><text x="160" y="114" textAnchor="middle">ABF</text></g>
  ];
  // One chapter now: the base, the draw and the cash. `page` stays so the art keys remain stable.
  const drawings = page === 0 ? [chapterOne[2], chapterOne[3], chapterTwo[0]] : chapterTwo;
  return <svg className="visual" viewBox="0 0 320 240" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
    <circle cx="160" cy="110" r="98" opacity=".08" />
    <motion.circle cx="160" cy="110" r="98" strokeDasharray="85 530" opacity=".45" animate={{ rotate: reduced ? 0 : active*72 }} transition={{ duration: reduced ? 0 : 1.5, ease: EASE }} style={{ transformOrigin: '160px 110px' }} />
    <AnimatePresence initial={false}><motion.g key={`${page}-${active}`} initial={enter} animate={{ opacity: 1, scale: 1, rotate: 0, filter: 'blur(0px)' }} exit={leave} transition={{ duration: reduced ? 0 : .85, ease: EASE }} style={{ transformOrigin: '160px 110px' }}>{drawings[active]}</motion.g></AnimatePresence>
  </svg>;
};
type ABFSlide = { title: string; text: string; panel: string; rows: string[]; sample?: boolean };
const ABF_PAGE = '/abf';
const abfPages: { mode: Mode; label: string; headline: React.ReactNode[]; slides: ABFSlide[] }[] = [
  { mode: 'dark', label: 'Asset-based finance · start with one facility', headline: ['Know the pool.', <>Know the <span className="mint">capacity.</span></>], slides: [
    { title: 'See past the certificate', text: 'The borrowing base is recalculated from the released pool and compared with the borrower’s certificate line by line. The certificate is evidence; the computed base governs.', panel: 'A difference worth seeing', rows: [], sample: true },
    { title: 'The draw waits for the covenant', text: 'Availability is derived on every read and held to zero by a named control. $4,990,520 before holds; $0 drawable while one covenant is in breach.', panel: 'Keep the funding decision in context', rows: ['Lower of commitment and approved base', 'Less drawn, less reservations', 'Held by the 30+ delinquency covenant'] },
    { title: 'The cash ties to the cent', text: 'What the tape implies, what the servicer reported and what the bank received, side by side. August: $1,185,380.81 on all three.', panel: 'One view of the collections', rows: ['Tape-implied collections', 'Servicer remittance detail', 'Bank receipts, matched loan by loan'] }
  ] }
];
const ABFScene: React.FC<{ page: number; p: number; select: (phase: number) => void }> = ({ page, p, select }) => {
  const reduced = !!useReducedMotion();
  const chapter = abfPages[page];
  const active = Math.min(p, chapter.slides.length - 1);
  const slide = chapter.slides[active];
  const names = ['Base', 'Draw', 'Cash'];
  return <ABFLayout>
    <div className="copy">
      <Fade show><MonoLabel>{chapter.label}</MonoLabel></Fade>
      <Headline small show lines={chapter.headline} />
      <div className="feature" style={{ display: 'grid' }} aria-live="polite">
        <AnimatePresence initial={false}>
          <motion.div key={active} style={{ gridArea: '1 / 1' }} initial={reduced ? false : { opacity: 0, filter: 'blur(7px)', clipPath: 'inset(0 100% 0 0)' }} animate={{ opacity: 1, filter: 'blur(0px)', clipPath: 'inset(0 0% 0 0)' }} exit={{ opacity: 0, filter: reduced ? 'blur(0px)' : 'blur(4px)' }} transition={{ duration: reduced ? 0 : .8, ease: EASE }}>
            <h2>{slide.title}</h2><p>{slide.text}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      <ABFSteps $m={chapter.mode} aria-label={`ABF page ${page + 1} scenes`}>
        {chapter.slides.map((item, idx) => <button key={item.title} onClick={() => select(idx)} aria-label={`${names[idx]}: ${item.title}`} aria-current={idx === active ? 'step' : undefined}>{names[idx]}</button>)}
        <a className="more" href={ABF_PAGE}>The full month, stage by stage <ArrowRight size={12} /></a>
      </ABFSteps>
    </div>
    <ABFPanel $m={chapter.mode}>
      <motion.div className="scan" key={active} initial={reduced ? false : { x: '-100%' }} animate={{ x: reduced ? '-100%' : '100%' }} transition={{ duration: 1.5, ease: EASE }} />
      <div className="head">AxiomFlow · ABF</div>
      <ABFArt page={page} active={active} reduced={reduced} />
      <div style={{ display: 'grid' }}>
        <AnimatePresence initial={false}>
          <motion.div key={active} style={{ gridArea: '1 / 1' }} initial={reduced ? false : { opacity: 0, filter: 'blur(5px)' }} animate={{ opacity: 1, filter: 'blur(0px)' }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : .7 }}>
            <h3>{slide.panel}</h3>
            {slide.sample ? <><div className="row"><span>Borrower’s certificate</span><b>$51.7M</b></div><div className="row"><span>Recalculated base</span><b className="accent">$50.7M</b></div><div className="row"><span>Difference surfaced</span><b>$1.0M</b></div><p className="note">Rounded results from an AxiomFlow sample $150M consumer-receivables facility. Recorded validation evidence; not a customer result.</p></> : <><div className="flow">{slide.rows.map((row, idx) => <div key={row}><b>0{idx + 1}</b><span>{row}</span></div>)}</div><p className="note">Workflow illustration · scope the walkthrough to your facility and mandate.</p></>}
          </motion.div>
        </AnimatePresence>
      </div>
    </ABFPanel>
  </ABFLayout>;
};

/* ------------------------------------------------------------- beats */
type Item = (shown: boolean) => React.ReactNode;
type Beat = { mode: Mode; center?: boolean; items?: (nav: () => void, authed: boolean, contact: (e?: React.SyntheticEvent) => void) => Item[]; custom?: (p: number, nav: () => void, authed: boolean, select: (phase: number) => void) => React.ReactNode; phases?: number };

const beats: Beat[] = [
  { mode: 'dark', center: false, phases: 2, custom: (p, nav, authed) => <HeroScene p={p} nav={nav} authed={authed} /> },
  { mode: 'dark', center: true, phases: NL + 2, custom: (p) => <ProblemStack p={p} /> },
  { mode: 'light', center: true, items: () => [
    (s) => <div style={{ display: 'grid', gap: 16, justifyItems: 'center' }}><Fade show={s}><MonoLabel>The enterprise answer</MonoLabel></Fade><Headline small show={s} lines={['A growing credit book.', <>An operating stack that <em>grows with it.</em></>]} /></div>,
    (s) => <CmpView show={s} />,
    (s) => <Fade show={s}><KickLine>Your teams need a <em>shared view.</em></KickLine></Fade>
  ] },
  { mode: 'dark', center: false, items: () => [
    (s) => <div style={{ display: 'grid', gap: 16 }}><Fade show={s}><MonoLabel>The model</MonoLabel></Fade><Headline show={s} lines={['So we built one', 'official book.']} /></div>,
    (s) => <Fade show={s}><Sub>Every acquisition becomes a governed trade — draft, approved, executed, posted — before it ever reaches your positions.</Sub></Fade>,
    (s) => <BlotterView show={s} />
  ] },
  { mode: 'dark', center: true, items: () => [
    (s) => <div style={{ display: 'grid', gap: 16, justifyItems: 'center' }}><Fade show={s}><MonoLabel>Coverage</MonoLabel></Fade><Headline show={s} lines={['Every asset class.', 'One lifecycle truth.']} /></div>,
    (s) => <CovView show={s} />,
    (s) => <Fade show={s}><Sub style={{ margin: '0 auto', textAlign: 'center' }}>Private credit is not a bond. CLO tranches are not generic securities. Each keeps its own domain rules.</Sub></Fade>
  ] },
  { mode: 'dark', center: false, phases: 4, custom: (p) => <MuscleScene p={p} /> },
  { mode: 'dark', center: false, phases: 3, custom: (p, _nav, _authed, select) => <ABFScene page={0} p={p} select={select} /> },
  { mode: 'dark', center: true, items: () => [
    (s) => <div style={{ display: 'grid', gap: 16, justifyItems: 'center' }}><Fade show={s}><MonoLabel>Who it's for</MonoLabel></Fade><Headline show={s} lines={['Built for the middle', 'market of credit.']} /></div>,
    (s) => <SegsView show={s} />,
    (s) => <Fade show={s}><MonoLabel style={{ opacity: 0.85, letterSpacing: '0.1em', textAlign: 'center' }}><span style={{ color: MINT }}>$1B – $100B AUM</span> · outgrown spreadsheets, priced out of the enterprise</MonoLabel></Fade>
  ] },
  { mode: 'dark', center: true, items: (nav, authed, contact) => [
    (s) => <div style={{ display: 'grid', gap: 16, justifyItems: 'center' }}><Fade show={s}><MonoLabel>The operating truth</MonoLabel></Fade><Headline show={s} lines={['One book. Every', 'credit workflow.']} /></div>,
    (s) => <Fade show={s}><div style={{ display: 'grid', gap: 22, justifyItems: 'center' }}><Sub style={{ margin: '0 auto', textAlign: 'center' }}>Every lifecycle event, every as-of question, every AI action — governed from the start.</Sub><CtaRow style={{ justifyContent: 'center' }}>{DEMO_BOOKING_ENABLED ? <PrimaryA href={CAL_URL} target="_blank" rel="noopener noreferrer">Book a demo <ArrowRight size={17} /></PrimaryA> : <PrimaryA href={CONTACT} onClick={contact}>Email us <ArrowRight size={17} /></PrimaryA>}<Quiet onClick={nav}>{authed ? 'Enter platform' : 'Sign in'}</Quiet><Quiet as="a" href={ABF_PAGE}>Start with one facility <ArrowRight size={14} /></Quiet></CtaRow><MonoLabel style={{ opacity: 0.6, letterSpacing: '0.1em', textTransform: 'none' }}>{DEMO_BOOKING_ENABLED ? <>or email </> : null}<a href={CONTACT} onClick={contact} style={{ color: 'inherit' }}>{CONTACT_EMAIL}</a></MonoLabel><MonoLabel style={{ opacity: 0.4, letterSpacing: '0.08em', textTransform: 'none', fontSize: 10 }}>AxiomFlow is a product of AnjaniGiri.</MonoLabel></div></Fade>
  ] }
];

/* ------------------------------------------------------------- shells */
const Col = styled.div<{ $center?: boolean }>`
  display: grid; gap: clamp(18px, 3vh, 32px); justify-items: ${p => (p.$center ? 'center' : 'start')};
  text-align: ${p => (p.$center ? 'center' : 'left')}; width: min(1000px, 100%);
`;
const Global = createGlobalStyle` html, body { background: #08080a; overscroll-behavior: none; } `;
const Root = styled(motion.div)` position: fixed; inset: 0; z-index: 1; overflow: clip; font-family: 'Inter', sans-serif; `;
const Grain = styled.div`
  position: absolute; inset: 0; pointer-events: none; opacity: 0.035; mix-blend-mode: overlay; z-index: 3;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;
const Glow = styled(motion.div)<{ $m: Mode }>`
  position: absolute; left: 50%; top: 44%; width: 62vw; height: 62vw; transform: translate(-50%, -50%);
  pointer-events: none; z-index: 1; border-radius: 50%;
  background: radial-gradient(circle, ${MINT}${p => (p.$m === 'dark' ? '16' : '10')}, transparent 62%); filter: blur(44px);
`;
const Frame = styled.div<{ $abf: boolean }>` position: absolute; inset: 0; z-index: 2; display: grid; place-items: center; padding: 118px clamp(24px, 7vw, 96px) 96px; ${p => p.$abf ? 'inset: 90px 0 74px; padding-block: 24px; overflow-y: auto; @media(max-width: 800px) { align-items: start; padding: 16px 22px; }' : ''} `;
const Header = styled.header` position: absolute; top: 0; left: 0; right: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between; padding: 22px clamp(24px, 5vw, 46px);
  @media (max-width: 600px) { padding: 18px 16px; gap: 8px; }
`;
const Brand = styled.button` border: 0; background: transparent; color: inherit; display: inline-flex; align-items: center; gap: 10px; cursor: pointer;
  span { font: 600 17px 'Fraunces', serif; letter-spacing: -0.01em; white-space: nowrap; }
  @media (max-width: 600px) { gap: 7px; span { font-size: 15px; } }
`;
const TopRight = styled.div` display: inline-flex; align-items: center; gap: 18px;
  @media (max-width: 600px) { gap: 8px; }
  button { border: 0; background: transparent; color: inherit; cursor: pointer; opacity: 0.62; font: 600 13px 'Inter', sans-serif; display: inline-flex; align-items: center; gap: 6px; }
  button:hover { opacity: 1; } `;
const QuietSm = styled.button`
  && { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; border-radius: 999px;
    border: 1px solid; background: transparent; color: inherit;
    font: 600 13px 'Inter', sans-serif; padding: 8px 15px; opacity: 0.55; transition: opacity .2s; white-space: nowrap; }
  &&:hover { opacity: 1; }
`;
const DemoBtn = styled.a`
  display: inline-flex; align-items: center; gap: 7px; cursor: pointer; text-decoration: none;
  border-radius: 999px; background: ${MINT}; color: #04120e; font: 700 13px 'Inter', sans-serif; padding: 9px 16px;
  white-space: nowrap; transition: transform .2s ease, box-shadow .3s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 30px ${MINT}55; }
`;
const Dots = styled.nav` position: absolute; right: 26px; top: 50%; transform: translateY(-50%); z-index: 20; display: grid; gap: 12px; align-items: center;
  @media (max-width: 760px){ display: none; } `;
const DotBtn = styled.button<{ $on: boolean }>` width: ${p => (p.$on ? 10 : 6)}px; height: ${p => (p.$on ? 10 : 6)}px; border-radius: 50%; border: 0; padding: 0; cursor: pointer;
  background: currentColor; opacity: ${p => (p.$on ? 1 : 0.28)}; transition: all .3s ease; `;
const Tick = styled.div<{ $on: boolean }>` width: 4px; height: 4px; border-radius: 50%; background: currentColor; opacity: ${p => (p.$on ? 0.95 : 0.22)}; margin: 0 auto; transition: opacity .3s; `;
const Foot = styled.div`
  position: absolute; left: 0; right: 0; bottom: 22px; z-index: 20; display: flex; align-items: center; justify-content: space-between;
  padding: 0 clamp(24px, 5vw, 46px); font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.12em; opacity: 0.4; text-transform: uppercase;
  .hint { display: inline-flex; align-items: center; gap: 8px; }
  @media (max-width: 760px){ .num { display: none; } }
`;

/* ------------------------------------------------------------- component */
const OMSLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const reduced = !!useReducedMotion();
  const enter = useCallback(() => navigate(isAuthenticated ? '/home' : '/login'), [navigate, isAuthenticated]);
  const [contactOpen, setContactOpen] = useState(false);
  const contactRef = useRef(false);
  const openContact = useCallback((e?: React.SyntheticEvent) => { e?.preventDefault(); contactRef.current = true; setContactOpen(true); }, []);
  const closeContact = useCallback(() => { contactRef.current = false; setContactOpen(false); }, []);

  const itemLists = useMemo(() => beats.map(bt => (bt.items ? bt.items(enter, isAuthenticated, openContact) : [])), [enter, isAuthenticated, openContact]);
  const counts = useMemo(() => beats.map((bt, i) => bt.phases ?? itemLists[i].length), [itemLists]);
  const steps = useMemo(() => {
    const s: Array<[number, number]> = [];
    counts.forEach((c, bi) => { for (let pi = 0; pi < c; pi++) s.push([bi, pi]); });
    return s;
  }, [counts]);
  const firstStep = useMemo(() => {
    const m: number[] = [];
    steps.forEach(([bi], sIdx) => { if (m[bi] === undefined) m[bi] = sIdx; });
    return m;
  }, [steps]);

  const [si, setSi] = useState(0);
  const clamped = Math.min(si, steps.length - 1);
  const [b, p] = steps[clamped] ?? [0, 0];
  const beat = beats[b];
  const theme = T[beat.mode];

  const lastAt = useRef(0);
  const acc = useRef(0);
  const touchY = useRef<number | null>(null);

  const stepBy = useCallback((d: number) => {
    const now = Date.now();
    if (now - lastAt.current < 720) return;
    lastAt.current = now;
    setSi(cur => Math.max(0, Math.min(steps.length - 1, cur + d)));
  }, [steps.length]);
  const goBeat = useCallback((bi: number) => { setSi(firstStep[bi] ?? 0); }, [firstStep]);

  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || contactRef.current) return;
      const scrollFrame = e.target instanceof HTMLElement ? e.target.closest('[data-abf-frame]') : null;
      if (scrollFrame && scrollFrame.scrollHeight > scrollFrame.clientHeight + 2 && ((e.deltaY > 0 && scrollFrame.scrollTop + scrollFrame.clientHeight < scrollFrame.scrollHeight - 2) || (e.deltaY < 0 && scrollFrame.scrollTop > 2))) return;
      e.preventDefault();
      acc.current += e.deltaY;
      if (Math.abs(acc.current) < 22) return;
      const d = acc.current > 0 ? 1 : -1; acc.current = 0; stepBy(d);
    };
    const onKey = (e: KeyboardEvent) => {
      if (contactRef.current) return;
      if (e.key === ' ' && e.target instanceof HTMLElement && e.target.closest('button, a, input, textarea, select')) return;
      if (['ArrowDown', 'PageDown', ' ', 'j'].includes(e.key)) { e.preventDefault(); stepBy(1); }
      else if (['ArrowUp', 'PageUp', 'k'].includes(e.key)) { e.preventDefault(); stepBy(-1); }
      else if (e.key === 'Home') { e.preventDefault(); setSi(0); }
      else if (e.key === 'End') { e.preventDefault(); setSi(steps.length - 1); }
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('wheel', onWheel); window.removeEventListener('keydown', onKey); };
  }, [stepBy, steps.length]);

  const onTouchStart = (e: React.TouchEvent) => { touchY.current = e.touches[0]?.clientY ?? null; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchY.current == null) return;
    const dy = touchY.current - (e.changedTouches[0]?.clientY ?? touchY.current);
    touchY.current = null;
    const scrollFrame = e.target instanceof HTMLElement ? e.target.closest('[data-abf-frame]') : null;
    if (scrollFrame && scrollFrame.scrollHeight > scrollFrame.clientHeight + 2) return;
    if (Math.abs(dy) > 46) stepBy(dy > 0 ? 1 : -1);
  };

  const items = itemLists[b];
  useEffect(() => {
    const oldTitle = document.title; document.title = 'AxiomFlow | One book for all of credit';
    return () => { document.title = oldTitle; };
  }, []);

  return (
    <Root onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} initial={false}
      animate={{ backgroundColor: theme.bg, color: theme.ink }} transition={{ duration: 0.9, ease: EASE }}>
      <Global />
      <Glow $m={beat.mode}
        animate={reduced
          ? { left: GLOW_POS[b][0], top: GLOW_POS[b][1] }
          : { left: GLOW_POS[b][0], top: GLOW_POS[b][1], scale: [1, 1.14, 1], opacity: [0.85, 1, 0.85] }}
        transition={{
          left: { duration: 1.5, ease: EASE }, top: { duration: 1.5, ease: EASE },
          scale: { duration: 15, repeat: Infinity, ease: 'easeInOut' }, opacity: { duration: 15, repeat: Infinity, ease: 'easeInOut' }
        }} />
      <Grain />

      <Header>
        <Brand onClick={() => setSi(0)}><AxiomFlowMark size={28} /><span>AxiomFlow</span></Brand>
        <TopRight>
          {isAuthenticated && <button onClick={logout}><LogOut size={14} /> Sign out</button>}
          <QuietSm as="a" href={ABF_PAGE} onClick={e => { e.preventDefault(); navigate(ABF_PAGE); }}>Asset-based finance</QuietSm>
          <QuietSm as="a" href={CONTACT} onClick={openContact}>Talk to us</QuietSm>
          <QuietSm onClick={enter}>{isAuthenticated ? 'Enter platform' : 'Sign in'}</QuietSm>
          {DEMO_BOOKING_ENABLED && <DemoBtn href={CAL_URL} target="_blank" rel="noopener noreferrer">Book a demo <ArrowRight size={13} /></DemoBtn>}
        </TopRight>
      </Header>

      <Frame $abf={b === 6} data-abf-frame={b === 6 ? true : undefined}>
        <AnimatePresence mode="wait">
          <motion.div key={b}
            initial={{ opacity: 0, y: 18, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -18, scale: 0.985, filter: 'blur(6px)' }}
            transition={{ duration: 0.6, ease: EASE }}
            style={{ width: 'min(1000px, 100%)', display: 'grid', justifyItems: 'center' }}>
            <Col $center={beat.center}>
              {beat.custom
                ? beat.custom(p, enter, isAuthenticated, phase => setSi((firstStep[b] ?? 0) + phase))
                : items.map((render, idx) => (
                  <div key={idx} style={{ width: beat.center ? 'auto' : '100%' }}>{render(idx <= p)}</div>
                ))}
            </Col>
          </motion.div>
        </AnimatePresence>
      </Frame>

      <Dots>
        {beats.map((_, idx) => (
          <div key={idx} style={{ display: 'grid', gap: 5, justifyItems: 'center' }}>
            <DotBtn $on={idx === b} onClick={() => goBeat(idx)} aria-label={`Section ${idx + 1}`} />
            {idx === b && counts[idx] > 1 && (
              <div style={{ display: 'grid', gap: 4 }}>
                {Array.from({ length: counts[idx] }).map((_, pi) => <Tick key={pi} $on={pi <= p} />)}
              </div>
            )}
          </div>
        ))}
      </Dots>

      <Foot>
        <span className="num">{String(b + 1).padStart(2, '0')} / {String(beats.length).padStart(2, '0')}</span>
        <span className="hint">{si < steps.length - 1 ? 'Scroll' : 'Fin'} <motion.span animate={reduced ? undefined : { y: [0, 4, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>↓</motion.span></span>
        <span>Control · Clarity · Confidence</span>
      </Foot>
      <ContactCard open={contactOpen} onClose={closeContact} subject={CONTACT_SUBJECT}
        title={<>Tell us what you run, and on what.</>}
        lede={<>Asset classes, fund administrator, where the spreadsheets still live. A line or two is enough to start.</>} />
    </Root>
  );
};

export default OMSLandingPage;
